import React, { useState, useEffect } from 'react';
import {
  School, GraduationCap, UserCheck, Bus, Plus, Trash2, RotateCcw,
  Check, Save, Clock, Globe, Calendar, Building, FileText,
  ShieldCheck, HeartPulse, Sparkles, Tag, ChevronRight
} from 'lucide-react';

export const DEFAULT_LOOKUPS = {
  // School defaults
  school_name: 'Apex Academy International School',
  school_code: 'AAIS-DEL-8942',
  school_affiliation: 'CBSE Affiliated #193028',
  school_campus: 'Main Campus - Sector 14',
  school_phone: '+91 (11) 2873-9900',
  school_email: 'transport@apexacademy.edu.in',
  school_address: 'Plot 42, Institutional Area, Sector 14, New Delhi - 110078',
  school_website: 'https://apexacademy.edu.in',
  school_academic_year: '2026 - 2027',
  school_shifts: [
    'Morning Shift (07:30 AM - 01:30 PM)',
    'Primary Shift (08:30 AM - 02:30 PM)',
    'After-School Co-Curricular (02:30 PM - 05:00 PM)'
  ],

  // Student master lookups
  student_grades: [
    'Pre-KG', 'LKG', 'UKG',
    'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
    'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
    'Class 11', 'Class 12'
  ],
  student_sections: ['A', 'B', 'C', 'D', 'E', 'F'],
  student_boards: ['CBSE', 'ICSE', 'State Board', 'IB (International Baccalaureate)', 'Cambridge (IGCSE)'],
  student_streams: [
    'Science (PCM)',
    'Science (PCB)',
    'Commerce with Math',
    'Commerce without Math',
    'Humanities / Arts',
    'Vocational / Applied Sciences'
  ],
  student_languages: [
    'Hindi', 'Sanskrit', 'French', 'German', 'Spanish',
    'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi', 'Urdu'
  ],
  student_houses: [
    'Red Tigers (Ruby)',
    'Blue Whales (Sapphire)',
    'Green Falcons (Emerald)',
    'Yellow Eagles (Topaz)'
  ],
  student_doc_types: [
    'Transit Pass',
    'Birth Certificate',
    'Aadhaar / National ID Card',
    'Medical Fitness & Immunization Record',
    'Transfer Certificate (TC)',
    'Address Proof',
    'Previous School Marksheet'
  ],

  // Driver master lookups
  driver_medical_statuses: [
    'Certified Fit',
    'Temporarily Unfit',
    'Medical Review Pending',
    'Expired / Re-exam Required',
    'Exempted / Special Permission'
  ],
  driver_police_statuses: [
    'Cleared & Verified',
    'Verification In-Progress',
    'Pending Department Clearance',
    'Disqualified / Adverse Record',
    'Expired / Re-verification Due'
  ],
  driver_doc_types: [
    'Commercial Driving License (HMV/Transport)',
    'Police Verification Certificate',
    'Annual Medical Fitness Certificate',
    'Aadhaar / National ID Card',
    'First Aid Training Certificate',
    'Defensive Driving Certification',
    'Eye Examination Report'
  ],

  // Bus master lookups
  bus_doc_statuses: [
    'Active / Valid',
    'Expiring Soon (Within 30 Days)',
    'Expired / Renewal Required',
    'Under Verification / Inspection',
    'Suspended / Blacklisted'
  ],
  bus_doc_types: [
    'Registration Certificate (RC)',
    'Commercial Vehicle Insurance Policy',
    'Fitness Certificate (Form 38)',
    'Pollution Under Control (PUC)',
    'State / All-India Route Permit',
    'Road Tax Clearance Certificate',
    'Speed Governor Calibration Certificate',
    'GPS Tracking Unit Fitness Certificate',
    'Fire Extinguisher Inspection Tag'
  ],
  bus_fuel_types: ['Diesel', 'CNG', 'Electric', 'Petrol / Hybrid'],
  bus_ownership_types: ['School Owned', 'Vendor Contract Leased', 'Private Contractor Bus']
};

export const GeneralSettingsTabs = ({
  backendUrl,
  timeFormat,
  setTimeFormat,
  dateFormat,
  setDateFormat,
  systemTimezone,
  setSystemTimezone,
  onLookupsUpdate
}) => {
  const [activeTab, setActiveTab] = useState('school'); // 'school' | 'students' | 'drivers' | 'buses'
  const [settings, setSettings] = useState(() => {
    // Load from localStorage or defaults
    const loaded = { ...DEFAULT_LOOKUPS };
    Object.keys(DEFAULT_LOOKUPS).forEach(key => {
      const saved = localStorage.getItem(`master_${key}`);
      if (saved !== null) {
        try {
          loaded[key] = JSON.parse(saved);
        } catch {
          loaded[key] = saved;
        }
      }
    });

    // Also sync legacy keys if present
    const legacyStudentDocs = localStorage.getItem('student_doc_types');
    if (legacyStudentDocs && !localStorage.getItem('master_student_doc_types')) {
      loaded.student_doc_types = legacyStudentDocs.split(',').map(s => s.trim()).filter(Boolean);
    }
    const legacyDriverDocs = localStorage.getItem('driver_doc_types');
    if (legacyDriverDocs && !localStorage.getItem('master_driver_doc_types')) {
      loaded.driver_doc_types = legacyDriverDocs.split(',').map(s => s.trim()).filter(Boolean);
    }

    return loaded;
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');
  const [newInputs, setNewInputs] = useState({});

  // Fetch persisted settings from backend if available
  useEffect(() => {
    let isMounted = true;
    const fetchRemoteSettings = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/settings/general`);
        if (!res.ok) return;
        const data = await res.json();
        if (data && data.success && data.settings && isMounted) {
          setSettings(prev => {
            const next = { ...prev };
            Object.keys(data.settings).forEach(k => {
              if (k.startsWith('master_')) {
                const subKey = k.replace('master_', '');
                next[subKey] = data.settings[k];
              } else if (DEFAULT_LOOKUPS[k] !== undefined) {
                next[k] = data.settings[k];
              }
            });
            return next;
          });
        }
      } catch (err) {
        // Silently use localStorage fallback
      }
    };

    fetchRemoteSettings();
    return () => { isMounted = false; };
  }, [backendUrl]);

  // Handle updates to string fields (like school info)
  const handleStringChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Handle adding an item to a lookup array
  const handleAddItem = (key) => {
    const val = (newInputs[key] || '').trim();
    if (!val) return;

    const list = Array.isArray(settings[key]) ? settings[key] : [];
    if (list.some(item => item.toLowerCase() === val.toLowerCase())) {
      alert(`"${val}" already exists in this lookup.`);
      return;
    }

    setSettings(prev => ({
      ...prev,
      [key]: [...list, val]
    }));
    setNewInputs(prev => ({ ...prev, [key]: '' }));
    setHasChanges(true);
  };

  // Handle deleting an item from a lookup array
  const handleDeleteItem = (key, index) => {
    const list = Array.isArray(settings[key]) ? settings[key] : [];
    const updated = list.filter((_, i) => i !== index);
    setSettings(prev => ({
      ...prev,
      [key]: updated
    }));
    setHasChanges(true);
  };

  // Reset a specific lookup key to its default
  const handleResetToDefault = (key) => {
    if (window.confirm(`Reset "${key.replace(/_/g, ' ')}" back to standard default values?`)) {
      setSettings(prev => ({
        ...prev,
        [key]: DEFAULT_LOOKUPS[key]
      }));
      setHasChanges(true);
    }
  };

  // Save all settings & master lookups
  const handleSaveAll = async (e) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    setSaveSuccessMessage('');

    try {
      // 1. Save to LocalStorage
      localStorage.setItem('time_format', timeFormat);
      localStorage.setItem('date_format', dateFormat);
      localStorage.setItem('system_timezone', systemTimezone);

      // Save legacy strings for backwards-compatibility with existing screens
      if (Array.isArray(settings.student_doc_types)) {
        localStorage.setItem('student_doc_types', settings.student_doc_types.join(','));
      }
      if (Array.isArray(settings.driver_doc_types)) {
        localStorage.setItem('driver_doc_types', settings.driver_doc_types.join(','));
      }

      // Save all master settings with prefix
      const payloadSettings = {
        time_format: timeFormat,
        date_format: dateFormat,
        system_timezone: systemTimezone
      };

      Object.entries(settings).forEach(([k, v]) => {
        localStorage.setItem(`master_${k}`, JSON.stringify(v));
        payloadSettings[`master_${k}`] = v;
      });

      // 2. Persist to Backend API
      try {
        await fetch(`${backendUrl}/api/settings/general`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ settings: payloadSettings })
        });
      } catch (err) {
        console.warn('Could not sync settings to backend API, cached in localStorage:', err);
      }

      if (onLookupsUpdate) {
        onLookupsUpdate(settings);
      }

      setHasChanges(false);
      setSaveSuccessMessage('All Master Lookups & General Settings saved successfully!');
      setTimeout(() => setSaveSuccessMessage(''), 4000);
    } catch (err) {
      alert('Error saving settings: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Render a reusable Lookup Card for arrays
  const renderLookupCard = (key, title, subtitle, icon, badgeColor = '#2563EB', isStatus = false) => {
    const items = Array.isArray(settings[key]) ? settings[key] : [];
    const inputVal = newInputs[key] || '';

    return (
      <div className="master-lookup-card">
        <div className="master-lookup-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="master-lookup-icon-badge">{icon}</span>
            <div>
              <h4 className="master-lookup-title">{title}</h4>
              <p className="master-lookup-subtitle">{subtitle}</p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span className="master-lookup-counter" style={{ backgroundColor: `${badgeColor}15`, color: badgeColor }}>
              {items.length} {items.length === 1 ? 'item' : 'items'}
            </span>
            <button
              type="button"
              className="master-lookup-reset-btn"
              onClick={() => handleResetToDefault(key)}
              title="Reset to default options"
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </div>

        {/* Tag / Chip Pills Area */}
        <div className="master-lookup-pills-container">
          {items.length === 0 ? (
            <div className="master-lookup-empty">No items added yet. Add one below.</div>
          ) : (
            items.map((item, index) => {
              // Determine status pill color styling if it's a status lookup
              let pillStyle = {};
              const lower = item.toLowerCase();
              if (isStatus) {
                if (lower.includes('fit') || lower.includes('clear') || lower.includes('active') || lower.includes('valid')) {
                  pillStyle = { backgroundColor: '#ECFDF5', color: '#059669', borderColor: '#A7F3D0' };
                } else if (lower.includes('soon') || lower.includes('pending') || lower.includes('progress') || lower.includes('review')) {
                  pillStyle = { backgroundColor: '#FFFBEB', color: '#D97706', borderColor: '#FDE68A' };
                } else if (lower.includes('unfit') || lower.includes('expired') || lower.includes('disqualified') || lower.includes('adverse') || lower.includes('suspend')) {
                  pillStyle = { backgroundColor: '#FEF2F2', color: '#DC2626', borderColor: '#FECACA' };
                }
              }

              return (
                <div key={index} className="master-lookup-pill" style={pillStyle}>
                  {isStatus && <span className="master-status-dot" style={{ backgroundColor: pillStyle.color || '#64748B' }} />}
                  <span className="master-lookup-pill-text">{item}</span>
                  <button
                    type="button"
                    className="master-lookup-pill-del"
                    onClick={() => handleDeleteItem(key, index)}
                    title={`Remove "${item}"`}
                  >
                    ×
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Inline Add Control */}
        <div className="master-lookup-add-box">
          <input
            type="text"
            className="master-lookup-input"
            placeholder={`Add new ${title.toLowerCase().replace(/s$/, '')}...`}
            value={inputVal}
            onChange={(e) => setNewInputs(prev => ({ ...prev, [key]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddItem(key);
              }
            }}
          />
          <button
            type="button"
            className="master-lookup-add-btn"
            onClick={() => handleAddItem(key)}
            disabled={!inputVal.trim()}
          >
            <Plus size={14} /> Add
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="master-settings-container">
      {/* Top Banner and Navigation */}
      <div className="master-settings-topbar">
        <div>
          <div className="master-settings-main-title">
            ⚙️ General Settings & Master Lookups
          </div>
          <div className="master-settings-main-desc">
            Configure institutional profile, academic student parameters, driver credentials, and fleet master lookups.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {hasChanges && (
            <span className="master-unsaved-badge">
              ● Unsaved Changes
            </span>
          )}
          <button
            type="button"
            className="settings-btn"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#2563EB',
              padding: '10px 20px',
              borderRadius: '12px',
              fontWeight: '700',
              fontSize: '13px',
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              cursor: isSaving ? 'not-allowed' : 'pointer'
            }}
            onClick={handleSaveAll}
            disabled={isSaving}
          >
            <Save size={16} /> {isSaving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>

      {/* Success Notification Alert */}
      {saveSuccessMessage && (
        <div className="master-alert-success" style={{ animation: 'fadeIn 0.2s ease-in' }}>
          <Check size={18} />
          <span>{saveSuccessMessage}</span>
        </div>
      )}

      {/* Horizontal Main Category Tabs */}
      <div className="master-tabs-nav">
        {[
          { id: 'school', label: 'School Profile & Timings', icon: <School size={16} />, count: 'School' },
          { id: 'students', label: 'Students Master Lookups', icon: <GraduationCap size={16} />, count: '7 Masters' },
          { id: 'drivers', label: 'Drivers Master Lookups', icon: <UserCheck size={16} />, count: '3 Masters' },
          { id: 'buses', label: 'Buses Master Lookups', icon: <Bus size={16} />, count: '4 Masters' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`master-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
            <span className="tab-badge">{tab.count}</span>
          </button>
        ))}
      </div>

      {/* TAB 1: SCHOOL & INSTITUTION */}
      {activeTab === 'school' && (
        <div className="master-tab-content">
          <div className="master-grid-2col">
            {/* Institution Profile */}
            <div className="settings-card">
              <div className="settings-title">
                <Building size={18} color="#2563EB" /> School Information
              </div>
              <div className="settings-form">
                <div className="form-group">
                  <label>Institution / School Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settings.school_name || ''}
                    onChange={(e) => handleStringChange('school_name', e.target.value)}
                    placeholder="e.g. Apex Academy International School"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>School Code / ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.school_code || ''}
                      onChange={(e) => handleStringChange('school_code', e.target.value)}
                      placeholder="e.g. AAIS-DEL-8942"
                    />
                  </div>
                  <div className="form-group">
                    <label>Affiliation / Board Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.school_affiliation || ''}
                      onChange={(e) => handleStringChange('school_affiliation', e.target.value)}
                      placeholder="e.g. CBSE Affiliated #193028"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Campus / Branch Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.school_campus || ''}
                      onChange={(e) => handleStringChange('school_campus', e.target.value)}
                      placeholder="e.g. Main Campus - Sector 14"
                    />
                  </div>
                  <div className="form-group">
                    <label>Academic Year</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.school_academic_year || ''}
                      onChange={(e) => handleStringChange('school_academic_year', e.target.value)}
                      placeholder="e.g. 2026 - 2027"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Contact Phone Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={settings.school_phone || ''}
                      onChange={(e) => handleStringChange('school_phone', e.target.value)}
                      placeholder="+91 (11) 2873-9900"
                    />
                  </div>
                  <div className="form-group">
                    <label>Transport / Official Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={settings.school_email || ''}
                      onChange={(e) => handleStringChange('school_email', e.target.value)}
                      placeholder="transport@school.edu"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Campus Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settings.school_address || ''}
                    onChange={(e) => handleStringChange('school_address', e.target.value)}
                    placeholder="Physical address with city and pin code"
                  />
                </div>

                <div className="form-group">
                  <label>Official Portal / Website</label>
                  <input
                    type="url"
                    className="form-control"
                    value={settings.school_website || ''}
                    onChange={(e) => handleStringChange('school_website', e.target.value)}
                    placeholder="https://apexacademy.edu.in"
                  />
                </div>
              </div>
            </div>

            {/* Regional Formats & Operating Shifts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Date & Time Preferences */}
              <div className="settings-card">
                <div className="settings-title">
                  <Globe size={18} color="#2563EB" /> System Preferences & Localization
                </div>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Date Display Format</label>
                    <select
                      className="form-control"
                      value={dateFormat}
                      onChange={(e) => {
                        setDateFormat(e.target.value);
                        setHasChanges(true);
                      }}
                    >
                      <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2026-08-17)</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 17/08/2026)</option>
                      <option value="MM-DD-YYYY">MM-DD-YYYY (e.g. 08-17-2026)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Time Display Format</label>
                    <select
                      className="form-control"
                      value={timeFormat}
                      onChange={(e) => {
                        setTimeFormat(e.target.value);
                        setHasChanges(true);
                      }}
                    >
                      <option value="12hr">12-Hour format (e.g. 07:30 AM / 02:15 PM)</option>
                      <option value="24hr">24-Hour format (e.g. 07:30 / 14:15)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>School Default Timezone</label>
                    <select
                      className="form-control"
                      value={systemTimezone}
                      onChange={(e) => {
                        setSystemTimezone(e.target.value);
                        setHasChanges(true);
                      }}
                    >
                      <option value="Asia/Kolkata">India Standard Time (IST) - UTC+5:30</option>
                      <option value="UTC">Coordinated Universal Time (UTC)</option>
                      <option value="America/New_York">US Eastern Time (EST/EDT) - UTC-5</option>
                      <option value="Asia/Dubai">Gulf Standard Time (GST) - UTC+4</option>
                      <option value="Europe/London">Greenwich Mean Time / BST (London)</option>
                      <option value="Asia/Singapore">Singapore Standard Time (SGT) - UTC+8</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Operational Shifts */}
              {renderLookupCard(
                'school_shifts',
                'School Operational Shifts / Timings',
                'Designate operational daily transport shifts and schedules',
                <Clock size={16} color="#2563EB" />,
                '#2563EB'
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: STUDENTS MASTER LOOKUPS */}
      {activeTab === 'students' && (
        <div className="master-tab-content">
          <div className="master-grid-2col">
            {/* 1. Grades / Standard */}
            {renderLookupCard(
              'student_grades',
              'Grades / Standards',
              'Academic classes enrolled for student transport routing',
              <GraduationCap size={16} color="#8B5CF6" />,
              '#8B5CF6'
            )}

            {/* 2. Section / Division */}
            {renderLookupCard(
              'student_sections',
              'Sections / Divisions',
              'Class section divisions (e.g., A, B, C, D)',
              <Tag size={16} color="#EC4899" />,
              '#EC4899'
            )}

            {/* 3. Board of Education */}
            {renderLookupCard(
              'student_boards',
              'Boards of Education',
              'Educational regulatory boards and accreditations',
              <Building size={16} color="#10B981" />,
              '#10B981'
            )}

            {/* 4. Academic Stream */}
            {renderLookupCard(
              'student_streams',
              'Academic Streams',
              'High school streams (Science, Commerce, Humanities)',
              <Sparkles size={16} color="#F59E0B" />,
              '#F59E0B'
            )}

            {/* 5. Optional Language */}
            {renderLookupCard(
              'student_languages',
              'Optional Languages',
              'Second and third language curriculum choices',
              <Globe size={16} color="#06B6D4" />,
              '#06B6D4'
            )}

            {/* 6. House Name */}
            {renderLookupCard(
              'student_houses',
              'House Names',
              'Student house division for sports and extracurriculars',
              <ShieldCheck size={16} color="#E11D48" />,
              '#E11D48'
            )}

            {/* 7. Types of Student Documents (Spanning Full Width) */}
            <div style={{ gridColumn: '1 / -1' }}>
              {renderLookupCard(
                'student_doc_types',
                'Types of Student Documents',
                'Allowed document types for student DMS uploads and identity records',
                <FileText size={16} color="#3B82F6" />,
                '#3B82F6'
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: DRIVERS MASTER LOOKUPS */}
      {activeTab === 'drivers' && (
        <div className="master-tab-content">
          <div className="master-grid-2col">
            {/* 1. Medical Fitness Status */}
            {renderLookupCard(
              'driver_medical_statuses',
              'Medical Fitness Statuses',
              'Status options for annual driver health and fitness certifications',
              <HeartPulse size={16} color="#10B981" />,
              '#10B981',
              true
            )}

            {/* 2. Police Verification Status */}
            {renderLookupCard(
              'driver_police_statuses',
              'Police Verification Statuses',
              'Background check and law enforcement clearance options',
              <ShieldCheck size={16} color="#2563EB" />,
              '#2563EB',
              true
            )}

            {/* 3. Driver Document Types (Full Width) */}
            <div style={{ gridColumn: '1 / -1' }}>
              {renderLookupCard(
                'driver_doc_types',
                'Driver Document Types',
                'Mandatory and optional compliance documents required for transport drivers',
                <FileText size={16} color="#F59E0B" />,
                '#F59E0B'
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: BUSES MASTER LOOKUPS */}
      {activeTab === 'buses' && (
        <div className="master-tab-content">
          <div className="master-grid-2col">
            {/* 1. Bus Document Status */}
            {renderLookupCard(
              'bus_doc_statuses',
              'Vehicle Document Statuses',
              'Validation and lifecycle states for fleet compliance documents',
              <ShieldCheck size={16} color="#10B981" />,
              '#10B981',
              true
            )}

            {/* 2. Fuel Types */}
            {renderLookupCard(
              'bus_fuel_types',
              'Fuel & Powertrain Types',
              'Engine and powertrain fuel classifications for school buses',
              <Bus size={16} color="#06B6D4" />,
              '#06B6D4'
            )}

            {/* 3. Ownership Types */}
            {renderLookupCard(
              'bus_ownership_types',
              'Bus Ownership Classifications',
              'Contractual and fleet asset ownership categories',
              <Building size={16} color="#8B5CF6" />,
              '#8B5CF6'
            )}

            {/* 4. Bus Document Types (Full Width) */}
            <div style={{ gridColumn: '1 / -1' }}>
              {renderLookupCard(
                'bus_doc_types',
                'Bus Compliance Document Types',
                'Statutory and technical document categories for vehicles (RC, Insurance, Fitness, PUC)',
                <FileText size={16} color="#2563EB" />,
                '#2563EB'
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
